import {
  computeFileChecksum,
  FILE_CHECKSUM_ALGORITHM,
} from '../computeFileChecksum'
import type { TEntityId } from '~/types/models'
import { httpPost, httpPatchMerge, httpDelete } from '~/services/http'
import type {
  TEntityDocument,
  TEntityUploadDocument,
} from '~/types/document/models'

export function makeDocumentService<
  T extends TEntityDocument,
  U extends TEntityUploadDocument
>(apiEntityPath: string, entityName: string) {
  async function createDocument(entity: U) {
    const callingMn = `${createDocument.name} (${entityName}) |`
    const apiPath = apiEntityPath
    if (!(entity.file instanceof File)) {
      throw new TypeError(
        `${callingMn} Called with no file or invalid File type`
      )
    }
    entity.fileChecksum = await computeFileChecksum(entity.file)
    entity.fileChecksumAlgorithm = FILE_CHECKSUM_ALGORITHM
    // version is server-assigned on create; it's only sent on update (optimistic
    // concurrency). Don't include it in the upload payload.
    delete entity.version

    // The file is uploaded as multipart/form-data (see httpPost). It must still
    // be on the entity when httpPost builds the request body. Afterwards, swap
    // the binary for the file metadata the rest of the app persists/displays:
    // saveEntityData stores this same entity object (only copying hrid/version
    // from the response), so the metadata has to live here, not just on the
    // server's reply. The real server derives the same metadata from the upload.
    const file = entity.file
    const response = await httpPost<U>({
      callingMn,
      apiPath,
      entity,
    })
    entity.filename = file.name
    entity.filesize = file.size
    entity.filetype = file.type
    // fileUrlPath is generated server-side (the stored file's URL) and can't be
    // derived from the local File like the metadata above. saveEntityData carries
    // only hrid/version across from the response, so copy it onto the entity here.
    ;(entity as TEntityDocument).fileUrlPath = (
      response as TEntityDocument
    ).fileUrlPath
    delete entity.file
    return response
  }
  async function updateDocument(entityData: Partial<T>, id: TEntityId) {
    const callingMn = `${updateDocument.name} (${entityName})`
    const apiPath = `${apiEntityPath}/${id}`
    return httpPatchMerge<Partial<T>>({
      callingMn,
      apiPath,
      entityData,
    })
  }
  async function deleteDocument(entityData: T) {
    const callingMn = `${deleteDocument.name} (${entityName})`
    const apiPath = apiEntityPath
    return httpDelete<T>({
      callingMn,
      apiPath,
      entityData,
    })
  }
  return { createDocument, updateDocument, deleteDocument }
}
